import { useReducer } from 'react';

import type { VehicleLookUpData } from 'components/PageWrappers/AdDetails/components/DealerEnquiryModal/DealerEnquiryModal.config';
import { ActionType } from 'components/PageWrappers/AdDetails/components/DealerEnquiryModal/DealerEnquiryModal.typed';
import {
  ACTION_TYPE,
  StateType,
} from 'components/PageWrappers/AdDetails/components/DealerEnquiryModal/DealerEnquiryModal.typed';
import type { InitialEnquiryModalValues } from 'types';

const initialState: StateType = {
  isSubmitting: false,
  disableSubmitButton: true,
  showKeyInfoList: false,
  disableVehicleRegLookupButton: false,
  vehicleLookupErrorMessage: '',
  tradeInValues: undefined,
};

const dealerEnquiryModalReducer = (
  state: StateType,
  action: ActionType,
): StateType => {
  switch (action.type) {
    case ACTION_TYPE.SET_IS_SUBMITTING:
      return {
        ...state,
        isSubmitting: action.payload,
      };
    case ACTION_TYPE.SET_DISABLE_SUBMIT_BUTTON:
      return {
        ...state,
        disableSubmitButton: action.payload,
      };
    case ACTION_TYPE.SET_SHOW_KEY_INFO_LIST:
      return {
        ...state,
        showKeyInfoList: action.payload,
      };
    case ACTION_TYPE.SET_TRADE_IN_VALUES:
      return {
        ...state,
        tradeInValues: action.payload,
      };
    case ACTION_TYPE.SET_VEHICLE_LOOKUP_ERROR_MESSAGE:
      return {
        ...state,
        vehicleLookupErrorMessage: action.payload,
      };
    case ACTION_TYPE.SET_DISABLE_VEHICLE_REG_LOOKUP_BUTTON:
      return {
        ...state,
        disableVehicleRegLookupButton: action.payload,
      };
    case ACTION_TYPE.ON_LOOKUP_SUCCESS:
      return {
        ...state,
        disableVehicleRegLookupButton: true,
        tradeInValues: action.payload,
        vehicleLookupErrorMessage: '',
      };
    case ACTION_TYPE.ON_LOOKUP_FAILURE:
      return {
        ...state,
        tradeInValues: undefined,
        vehicleLookupErrorMessage: action.payload,
      };
    case ACTION_TYPE.POPULATE_MODAL:
      return {
        ...state,
        disableVehicleRegLookupButton: true,
        tradeInValues: action.payload,
      };
    case ACTION_TYPE.RESET_MODAL:
      return {
        ...action.payload,
      };
    default:
      return state;
  }
};

function useDealerEnquiryModalReducer() {
  const [state, dispatch] = useReducer(dealerEnquiryModalReducer, initialState);

  const setIsSubmitting = (value: boolean) =>
    dispatch({
      type: ACTION_TYPE.SET_IS_SUBMITTING,
      payload: value,
    });

  const setDisableSubmitButton = (value: boolean) =>
    dispatch({
      type: ACTION_TYPE.SET_DISABLE_SUBMIT_BUTTON,
      payload: value,
    });

  const setShowKeyInfoList = (value: boolean) =>
    dispatch({
      type: ACTION_TYPE.SET_SHOW_KEY_INFO_LIST,
      payload: value,
    });

  const setTradeInValues = (value: VehicleLookUpData) =>
    dispatch({
      type: ACTION_TYPE.SET_TRADE_IN_VALUES,
      payload: value,
    });

  const setVehicleLookUpErrorMessage = (value: string) =>
    dispatch({
      type: ACTION_TYPE.SET_VEHICLE_LOOKUP_ERROR_MESSAGE,
      payload: value,
    });

  const setDisableVehicleRegLookupButton = (value: boolean) =>
    dispatch({
      type: ACTION_TYPE.SET_DISABLE_VEHICLE_REG_LOOKUP_BUTTON,
      payload: value,
    });

  const resetModal = () => {
    dispatch({
      type: ACTION_TYPE.RESET_MODAL,
      payload: {
        isSubmitting: false,
        disableSubmitButton: false,
        showKeyInfoList: false,
        disableVehicleRegLookupButton: false,
      },
    });
  };

  const onLookupSuccess = (tradeInValues?: VehicleLookUpData) => {
    dispatch({
      type: ACTION_TYPE.ON_LOOKUP_SUCCESS,
      payload: tradeInValues,
    });
  };

  const onLookupFailure = (vehicleLookupErrorMessage: string) => {
    dispatch({
      type: ACTION_TYPE.ON_LOOKUP_FAILURE,
      payload: vehicleLookupErrorMessage,
    });
  };

  const populateModal = (enquiry?: InitialEnquiryModalValues) => {
    dispatch({
      type: ACTION_TYPE.POPULATE_MODAL,
      payload: enquiry,
    });
  };

  return {
    ...state,
    setIsSubmitting,
    setDisableSubmitButton,
    setShowKeyInfoList,
    setTradeInValues,
    setVehicleLookUpErrorMessage,
    setDisableVehicleRegLookupButton,
    resetModal,
    onLookupSuccess,
    onLookupFailure,
    populateModal,
  };
}

export { useDealerEnquiryModalReducer };
